import IconButton from '@mui/material/IconButton';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Row,
} from '@tanstack/react-table';
import {
  formatDateTime,
} from 'utils/date';
import {
  useNavigate,
} from 'react-router-dom';
import NotesIcon from 'core/icons/notes';
import Table from 'core/components/VirtualTable';
import Name from 'pages/Dashboard/components/Name';
import compact from 'lodash/compact';
import MultiMediaList from 'pages/Dashboard/components/MultimediaList';
import {
  MediaOriginatorType,
  MultimediaType,
} from 'pages/Dashboard/utils/constants';
import ToolTip from 'core/components/Tooltip';
import {
  useMedia,
  useMixpanelTrack,
  useSidePanel,
} from 'core/hooks';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';
import Spinner from 'pages/Dashboard/components/Spinner';
import Addendum from 'pages/Dashboard/pages/Charts/pages/Details/components/VisitsAndNotes/Addendum';
import {
  Props,
} from 'pages/Dashboard/pages/Charts/pages/Details/types/tabContentProps';
import {
  PatientVisitDTO,
} from 'dtos';
import {
  useGetUpcomingAppointments,
  useGetHistoricalVisitNotesByPatientId,
} from 'pages/Dashboard/pages/Charts/pages/Details/hooks';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import AddendumDialog, {
  AddendumDialogProps,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/VisitsAndNotes/AddendumDialog';
import isNil from 'lodash/isNil';
import {
  isNilOrZero,
} from '@populateui/utils';
import Image from 'pages/Dashboard/components/Image';
import OfficeIcon from '@mui/icons-material/BusinessRounded';
import {
  useColorsHashMap,
} from 'pages/Dashboard/pages/Encounters/hooks';
import {
  normalizePatientVisits,
} from 'pages/Dashboard/pages/Charts/pages/Details/utils/helper';
import {
  toEventPanelInfo,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/eventPanelUrl';
import {
  ViewContext,
} from 'pages/Dashboard/contexts/viewContext';
import useGetPatientMediaByMultimediaType from 'pages/Dashboard/hooks/useGetPatientMediaByMultimediaType';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  useAppointmentsRefresh,
} from 'pages/Dashboard/hooks/usePatientSummaryCount';

export type PatientVisit = PatientVisitDTO & {
  serviceFacilityColor: string;
};

type VisitActionsCellProps = {
  row: Row<PatientVisit>;
  isPastVisit?: boolean;
};

function VisitActionsCell({ row, isPastVisit }: VisitActionsCellProps) {
  const navigate = useNavigate();
  const mixpanelTrack = useMixpanelTrack();

  return (
    <div className="flex justify-end w-full">
      {isPastVisit && <Addendum visitInfo={row.original} />}
      <ToolTip title="Open Visit Note">
        <IconButton
          color="info"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/encounters/${row.original.encounterId}`);
            mixpanelTrack('navigatedToVisitNote', { source: 'Charts' });
          }}
        >
          <NotesIcon className="!h-4 !w-4 text-blue-500" />
        </IconButton>
      </ToolTip>
    </div>
  );
}

function getVisitActionsColumn(isPastVisit?: boolean): ColumnDef<PatientVisit> {
  return {
    id: 'actions',
    header: '',
    size: 80,
    minSize: 60,
    cell: ({ row }) => <VisitActionsCell row={row} isPastVisit={isPastVisit} />,
  };
}

type ProviderCellProps = {
  row: Row<PatientVisit>;
};

function ProviderCell({ row: { original } }: ProviderCellProps) {
  const src = useUserProfilePicturePath(original.provider?.userId);

  return (
    <Name
      image={{ src }}
      {...original.provider?.formattedNames}
      placeholder={DEFAULT_EMPTY_VALUE}
    />
  );
}

const upcomingAppointmentColumns: ColumnDef<PatientVisit>[] = [{
  id: 'date',
  accessorKey: 'visitDateTime',
  header: 'Date',
  size: 100,
  cell: ({ row: { original } }) => (
    <span className="max-w-[60px]">
      {formatDateTime(original.visitDateTime ?? '')}
    </span>
  ),
}, {
  id: 'diagnosis',
  accessorKey: 'reasonForVisit',
  header: 'Reason for visit',
  meta: { autoSized: true },
  minSize: 120,
  cell: ({ row: { original } }) => (
    <ToolTip
      placement="top-start"
      title={original.reasonForVisit ?? ''}
    >
      <p className="max-w-fit truncate">{original.reasonForVisit ?? '--'}</p>
    </ToolTip>
  ),
}, {
  id: 'patientAppointmentStatus',
  header: 'Status',
  size: 170,
  cell: ({ row: { original: { patientAppointmentStatus } } }) => (
    <span className="font-semibold">{patientAppointmentStatus ?? ''}</span>
  ),
}, {
  id: 'typeOfVisitDescription',
  header: 'Type',
  size: 170,
  cell: ({ row: { original: { typeOfVisitDescription } } }) => (typeOfVisitDescription ?? ''),
}, {
  id: 'provider',
  header: 'Provider',
  size: 220,
  minSize: 150,
  cell: ({ row }) => <ProviderCell row={row} />,
}, {
  id: 'serviceFacility',
  header: 'Facility',
  minSize: 150,
  cell: ({ row: { original: { serviceFacilityColor, serviceFacility } } }) => (
    <div className="flex items-center gap-2">
      <Image
        key={serviceFacility?.serviceFacilityId}
        size="xs"
        className="font-semibold"
        color={serviceFacilityColor ?? ''}
      >
        <OfficeIcon className="!w-4 !h-4 !text-white" />
      </Image>
      <span>{serviceFacility?.nickname ?? ''}</span>
    </div>
  ),
}];

const pastVisitColumns: ColumnDef<PatientVisit>[] = [
  ...upcomingAppointmentColumns,
  getVisitActionsColumn(true),
];

export default function VisitsAndNotes({ patient }: Props) {
  const { patientId: id } = patient;
  const { isPanel } = useContext(ViewContext);
  const { openPanel } = useSidePanel();
  const {
    data: historicalVisitNotesData = [],
    isLoading: isLoadingHistoricalVisitNotes,
    refetch: refetchPastVisits,
  } = useGetHistoricalVisitNotesByPatientId(id);

  const {
    data: upcomingAppointmentsData = [],
    isLoading: isLoadingUpcomingAppointments,
    refetch: refetchUpcomingVisits,
  } = useGetUpcomingAppointments(id, { includeCancelledNoShow: true });

  const colorsHashMap = useColorsHashMap();

  const historicalVisits = useMemo(
    () => normalizePatientVisits(historicalVisitNotesData, colorsHashMap),
    [historicalVisitNotesData],
  );

  const upcomingAppointments = useMemo(
    () => normalizePatientVisits(upcomingAppointmentsData, colorsHashMap),
    [upcomingAppointmentsData],
  );

  const { requestMedia } = useMedia();

  useEffect(() => {
    const userIds = compact([
      ...upcomingAppointments.map((item) => item.provider?.userId),
      ...historicalVisits.map((item) => item.provider?.userId),
    ]);
    if (userIds.length > 0) {
      requestMedia(userIds, MediaOriginatorType.User);
    }
  }, [upcomingAppointments, historicalVisits, requestMedia]);

  const {
    data: mediaList = [],
    refetch,
  } = useGetPatientMediaByMultimediaType(
    patient.patientId ?? 0,
    [MultimediaType.FinalizedVisitNote],
    { generateUrls: false },
  );

  const hasData = useMemo(
    () => upcomingAppointments.length || historicalVisits.length,
    [upcomingAppointments.length, historicalVisits.length],
  );

  const isLoading = useMemo(
    () => isLoadingUpcomingAppointments || isLoadingHistoricalVisitNotes,
    [isLoadingUpcomingAppointments, isLoadingHistoricalVisitNotes],
  );

  useEffect(() => {
    const unsubscribe = visitNoteEventChannel.on('onAddendumPosted', () => { refetch(); });
    return () => { unsubscribe(); };
  }, []);

  const openAppointmentDetails = (data: PatientVisitDTO) => {
    const encounterId = data?.encounterId ?? 0;
    if (!isNilOrZero(encounterId)) {
      openPanel('event', toEventPanelInfo({
        id: encounterId,
        eventDate: data?.visitDateTime,
      }), { canGoBack: isPanel });
    }
  };

  const [
    addendumDialogProps,
    setAddendumDialogProps,
  ] = useState<AddendumDialogProps | null>(null);

  const refetchVisits = useCallback(async () => {
    await Promise.all([
      refetchUpcomingVisits(),
      refetchPastVisits(),
    ]);
  }, [refetchPastVisits, refetchUpcomingVisits]);

  useAppointmentsRefresh({
    enabled: true,
    onCreate: refetchVisits,
    onDelete: refetchVisits,
    onUpdate: refetchVisits,
  });

  useEffect(() => {
    const unsubscribeFromAddAddendum = visitNoteEventChannel.on('onAddAddendum', (params) => {
      setAddendumDialogProps(params);
    });

    return () => {
      unsubscribeFromAddAddendum();
    };
  }, []);

  return (
    <>
      <div>
        {!hasData && !isLoading && (
        <span className="text-sm text-center w-[35rem] nw-no-visit-note-indicator">
          No Visit Notes
        </span>
        )}
        {isLoading && (
        <div className="flex flex-auto justify-start items-center text-xs">
          <Spinner className="!font-bold mr-2" size={20} />
          Loading Visits...
        </div>
        )}
        {upcomingAppointmentsData.length > 0 && (
        <div className="flex flex-col flex-auto">
          <h4 className="text-sm font-semibold text-gray-400 mb-4">
            Upcoming Visits
          </h4>
          <Table<PatientVisit>
            getRowId={(row) => row.encounterId}
            columns={[...upcomingAppointmentColumns, getVisitActionsColumn()]}
            data={upcomingAppointments}
            classes={{
              outerRow: '!w-full',
              windowsClassName: '!w-full',
              scrollbarGutter: 'initial',
            }}
            onRowClick={(table, row) => {
              openAppointmentDetails(row?.original);
            }}
          />
        </div>
        )}
        {historicalVisits.length > 0 && (
        <div className="flex flex-col flex-auto">
          <h4 className="text-sm font-semibold text-gray-400 mb-4">
            Past Visits
          </h4>
          <Table<PatientVisit>
            getRowId={(row) => row.encounterId}
            columns={pastVisitColumns}
            data={historicalVisits}
            onRowClick={(table, row) => {
              openAppointmentDetails(row?.original);
            }}
            classes={{
              outerRow: '!w-full',
              windowsClassName: '!w-full',
              scrollbarGutter: 'initial',
            }}
          />
        </div>
        )}
        {mediaList.length > 0 && (
        <h4 className="text-sm font-semibold text-gray-400 mb-4">
          Finalized Visit Note PDFs
        </h4>
        )}
        <MultiMediaList
          className="w-3/4"
          data={mediaList ?? []}
        />
      </div>
      {!isNil(addendumDialogProps) && (
      <AddendumDialog
        {...addendumDialogProps}
        onClose={() => { setAddendumDialogProps(null); }}
      />
      )}
    </>
  );
}
